import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * GlobalTooltip
 * A single portal-less floating tooltip rendered at the top of the React tree.
 * Listens globally for mouseenter/focusin on any element with `data-tooltip="..."`.
 * Auto-positions to avoid viewport clipping (flips top/bottom/left/right as needed).
 */
const MARGIN = 8; // gap between target and tooltip
const VIEWPORT_PADDING = 6;

const GlobalTooltip = () => {
  const [state, setState] = useState({
    visible: false,
    text: '',
    top: 0,
    left: 0,
    placement: 'top',
  });
  const tooltipRef = useRef(null);
  const currentTargetRef = useRef(null);

  const computePosition = useCallback((target, text) => {
    const tip = tooltipRef.current;
    if (!tip) return null;

    // Temporarily make measurable
    tip.style.visibility = 'hidden';
    tip.style.display = 'block';
    tip.textContent = text;

    const rect = target.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Preferred: above, centered
    let placement = 'top';
    let top = rect.top - tipRect.height - MARGIN;
    let left = rect.left + rect.width / 2 - tipRect.width / 2;

    // Flip to bottom if not enough space above
    if (top < VIEWPORT_PADDING) {
      placement = 'bottom';
      top = rect.bottom + MARGIN;
    }

    // If still doesn't fit vertically, try side placements
    if (top + tipRect.height > vh - VIEWPORT_PADDING) {
      // Try right
      const rightLeft = rect.right + MARGIN;
      const leftLeft = rect.left - tipRect.width - MARGIN;
      const sideTop = rect.top + rect.height / 2 - tipRect.height / 2;
      if (rightLeft + tipRect.width <= vw - VIEWPORT_PADDING) {
        placement = 'right';
        top = sideTop;
        left = rightLeft;
      } else if (leftLeft >= VIEWPORT_PADDING) {
        placement = 'left';
        top = sideTop;
        left = leftLeft;
      }
    }

    // Clamp horizontally into viewport
    if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
    if (left + tipRect.width > vw - VIEWPORT_PADDING) {
      left = vw - VIEWPORT_PADDING - tipRect.width;
    }
    // Clamp vertically
    if (top < VIEWPORT_PADDING) top = VIEWPORT_PADDING;
    if (top + tipRect.height > vh - VIEWPORT_PADDING) {
      top = vh - VIEWPORT_PADDING - tipRect.height;
    }

    tip.style.visibility = '';
    tip.style.display = '';

    return { top, left, placement };
  }, []);

  const show = useCallback(
    (target) => {
      if (!target || !(target instanceof Element)) return;
      const text = target.getAttribute('data-tooltip');
      if (!text) return;
      currentTargetRef.current = target;

      // Defer to next frame so tooltip element exists with text for measurement
      requestAnimationFrame(() => {
        const pos = computePosition(target, text);
        if (!pos) return;
        setState({ visible: true, text, ...pos });
      });
    },
    [computePosition]
  );

  const hide = useCallback(() => {
    currentTargetRef.current = null;
    setState((s) => ({ ...s, visible: false }));
  }, []);

  useEffect(() => {
    const findTooltipTarget = (el) => {
      while (el && el !== document.body) {
        if (el.nodeType === 1 && el.hasAttribute && el.hasAttribute('data-tooltip')) {
          return el;
        }
        el = el.parentElement;
      }
      return null;
    };

    const onMouseOver = (e) => {
      const target = findTooltipTarget(e.target);
      if (!target) return;
      if (currentTargetRef.current === target) return;
      show(target);
    };

    const onMouseOut = (e) => {
      const target = findTooltipTarget(e.target);
      if (!target) return;
      // If moving within same target, ignore
      const related = e.relatedTarget;
      if (related && target.contains(related)) return;
      if (currentTargetRef.current === target) hide();
    };

    const onFocusIn = (e) => {
      const target = findTooltipTarget(e.target);
      if (target) show(target);
    };

    const onFocusOut = (e) => {
      const target = findTooltipTarget(e.target);
      if (target && currentTargetRef.current === target) hide();
    };

    const onScrollOrResize = () => {
      if (currentTargetRef.current) {
        const target = currentTargetRef.current;
        if (!document.body.contains(target)) {
          hide();
          return;
        }
        const text = target.getAttribute('data-tooltip');
        if (!text) {
          hide();
          return;
        }
        const pos = computePosition(target, text);
        if (pos) setState({ visible: true, text, ...pos });
      }
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') hide();
    };

    document.addEventListener('mouseover', onMouseOver, true);
    document.addEventListener('mouseout', onMouseOut, true);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mouseover', onMouseOver, true);
      document.removeEventListener('mouseout', onMouseOut, true);
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [show, hide, computePosition]);

  return (
    <div
      ref={tooltipRef}
      role="tooltip"
      aria-hidden={!state.visible}
      className="exlva-global-tooltip"
      data-placement={state.placement}
      style={{
        position: 'fixed',
        top: `${state.top}px`,
        left: `${state.left}px`,
        opacity: state.visible ? 1 : 0,
        pointerEvents: 'none',
        transform: state.visible ? 'translateY(0)' : 'translateY(2px)',
      }}
    >
      {state.text}
    </div>
  );
};

export default GlobalTooltip;
